import { VoiceTab } from '@avatar/contexts/tabs';
import {
  Project,
  ProjectEdge,
} from '../../../server/graphql/__generated.types';
import {
  EventName,
  useEventsContext,
} from '../../context/events/EventsContext';
import { DriveType } from '../Drives/types';
import { ProjectCreationSource } from '../../services/eventTrackingServiceTypes';
import { ProjectFragment } from '../fragments.generated';
import { DEFAULT_SUGGESTED_PROJECTS_PAGE_SIZE } from '../ProjectsV2/List/custom/hooks/usePaginatedSuggestedProjects';
import useProjectsOrderBy from './Listing/NewProjectsList/useProjectsOrderBy';
import {
  PersonalProjectsDocument,
  PersonalProjectsQuery,
  PersonalProjectsQueryVariables,
  SuggestedProjectsDocument,
  SuggestedProjectsQuery,
  SuggestedProjectsQueryVariables,
  useCreateProjectMutation,
  useDeleteProjectMutation,
} from './operation.generated';

export const useCreateProject = (
  {
    driveType,
    source,
    tab,
    variantId,
  }: {
    driveType?: DriveType;
    source: ProjectCreationSource;
    tab?: VoiceTab;
    variantId?: string;
  },
  options: Parameters<typeof useCreateProjectMutation>[0] = {}
) => {
  const { orderBy } = useProjectsOrderBy();

  const [eventTracking] = useEventsContext();
  return useCreateProjectMutation({
    update: (proxy, { data }) => {
      if (data && data.createProject.success && data.createProject.project) {
        const suggestedProjectsQueryData = proxy.readQuery<
          SuggestedProjectsQuery,
          SuggestedProjectsQueryVariables
        >({ query: SuggestedProjectsDocument });

        const queryData_V2 = proxy.readQuery<
          PersonalProjectsQuery,
          PersonalProjectsQueryVariables
        >({
          query: PersonalProjectsDocument,
          variables: {
            inputs: {
              orderBy,
            },
          },
        });
        if (queryData_V2) {
          const projects: Array<ProjectFragment> = [];
          if (queryData_V2.user.personalProjects.edges) {
            for (const edge of queryData_V2.user.personalProjects.edges) {
              projects.push(edge.node);
            }
          }
          projects.unshift(data.createProject.project);
          const edges: Array<ProjectEdge> = projects.map(p => ({
            __typename: 'ProjectEdge',
            node: p as Project, // ProjectFragment rather than Project
            cursor: '',
          }));
          proxy.writeQuery<
            PersonalProjectsQuery,
            PersonalProjectsQueryVariables
          >({
            query: PersonalProjectsDocument,
            variables: {
              inputs: {
                orderBy,
              },
            },
            data: {
              ...queryData_V2,
              user: {
                ...queryData_V2.user,
                personalProjects: {
                  __typename: 'ProjectsConnection',
                  ...queryData_V2.user.personalProjects,
                  edges,
                },
              },
            },
          });
        }

        if (suggestedProjectsQueryData) {
          const projects: Array<ProjectFragment> = [];
          if (suggestedProjectsQueryData.user.suggestedProjects.edges) {
            for (const edge of suggestedProjectsQueryData.user.suggestedProjects
              .edges) {
              projects.push(edge.node);
            }
          }
          projects.unshift(data.createProject.project);
          const limitedProjects = [...projects].slice(
            0,
            DEFAULT_SUGGESTED_PROJECTS_PAGE_SIZE
          );

          const edges: Array<ProjectEdge> = limitedProjects.map(p => ({
            __typename: 'ProjectEdge',
            node: p as Project,
            cursor: '',
          }));
          proxy.writeQuery<
            SuggestedProjectsQuery,
            SuggestedProjectsQueryVariables
          >({
            query: SuggestedProjectsDocument,
            variables: {},
            data: {
              ...suggestedProjectsQueryData,
              user: {
                ...suggestedProjectsQueryData.user,
                suggestedProjects: {
                  __typename: 'ProjectsConnection',
                  ...suggestedProjectsQueryData.user.suggestedProjects,
                  edges,
                },
              },
            },
          });
        }
      }
    },
    refetchQueries: [
      {
        query: PersonalProjectsDocument,
        variables: {
          inputs: {
            orderBy,
          },
        },
      },
    ],
    onCompleted: data => {
      if (data?.createProject.success) {
        eventTracking.track({
          type: EventName.ProjectCreated,
          data: {
            withClientName: Boolean(data?.createProject.project?.clientName),
            studioVersion: data?.createProject.project?.studioVersion,
            driveType,
            source,
            tab,
            variantId,
          },
        });
      }
    },
    ...options,
  });
};

export const useDeleteProject = (
  options: Parameters<typeof useDeleteProjectMutation>[0] = {}
) => {
  return useDeleteProjectMutation({
    ...options,
  });
};
